import { FC, useMemo, useState } from 'react';
import { useInView } from 'react-intersection-observer';

import { Card, FloatingTab, FloatingTabs } from '@dbt-labs/sourdough';

import {
  ChartDateDropdown,
  DateRange,
  DEFAULT_DATE_RANGE,
} from '@/components/Charts/ChartDateDropdown';
import {
  PercentageFailureChart,
  PercentageFailureEntry,
} from '@/components/Charts/PercentageFailureChart';
import { useDbtCloudProject } from '@/context/DbtCloudProjectProvider';
import { useMostExecutionFailedModels } from '@/hooks/discovery/performance/mostExecutionFailedModels';
import { useMostTestFailedModels } from '@/hooks/discovery/performance/mostTestFailedModels';
import { parseUniqueId } from '@/typings/parseUniqueId';

type MostFailedModelsChartsProps = {};

type TabType = 'build' | 'test';

export const MostFailedModelsCharts: FC<MostFailedModelsChartsProps> = () => {
  const { environmentId } = useDbtCloudProject();
  const [dateRange, setDateRange] = useState<DateRange>(DEFAULT_DATE_RANGE);
  const [activeTab, setActiveTab] = useState<TabType>('build');
  const { ref, inView } = useInView({ triggerOnce: true });

  const [executionFailedModels] = useMostExecutionFailedModels({
    environmentId: environmentId,
    startDate: dateRange.startDate,
    endDate: dateRange.endDate,
    limit: 5,
    pause: activeTab !== 'build' || !inView,
  });
  const [testFailedModels] = useMostTestFailedModels({
    environmentId: environmentId,
    startDate: dateRange.startDate,
    endDate: dateRange.endDate,
    limit: 5,
    pause: activeTab !== 'test' || !inView,
  });

  const rawData = useMemo(() => {
    if (activeTab === 'build') {
      return executionFailedModels.data?.performance?.mostExecutionFailedModels?.map(
        (node): PercentageFailureEntry => ({
          name: parseUniqueId(node.uniqueId).remainder || node.uniqueId,
          uniqueId: node.uniqueId!,
          totalFailedExecutions: node.totalFailedExecutions || 0,
          totalExecutions: node.totalExecutions || 0,
          failurePercentage: node.failurePercentage || 0,
          isDeleted: node.isDeleted!,
        }),
      );
    } else if (activeTab === 'test') {
      return testFailedModels.data?.performance?.mostTestFailedModels?.map(
        (node): PercentageFailureEntry => ({
          name: parseUniqueId(node.uniqueId).remainder || node.uniqueId,
          uniqueId: node.uniqueId!,
          totalFailedExecutions: node.totalRunsWithTestFailures || 0,
          totalExecutions: node.totalRunsWithTests || 0,
          failurePercentage: node.failurePercentage || 0,
          isDeleted: node.isDeleted!,
        }),
      );
    }
  }, [
    activeTab,
    executionFailedModels.data?.performance?.mostExecutionFailedModels,
    testFailedModels.data?.performance?.mostTestFailedModels,
  ]);

  return (
    <Card testId="most-failed-models-charts-container-card">
      <div data-testid="most-failed-models-charts-container" ref={ref}>
        <div className="mb-5 flex">
          <div className="flex-1 text-xl">Most failed models</div>
          <ChartDateDropdown onChange={setDateRange} />
        </div>
        <div className="overflow-x-auto">
          <FloatingTabs testId="most-failed-models-chart-tabs">
            <FloatingTab
              text="Build"
              id="build"
              isActive={activeTab === 'build'}
              onClick={() => setActiveTab('build')}
              testId="most-failed-models-chart-tabs-build"
            />
            <FloatingTab
              text="Test"
              id="test"
              isActive={activeTab === 'test'}
              onClick={() => setActiveTab('test')}
              testId="most-failed-models-chart-tabs-test"
            />
          </FloatingTabs>
        </div>
        <div className="h-[350px]">
          {activeTab === 'build' && (
            <PercentageFailureChart
              missingDataMessage="None of your models have failed to build in this time period."
              xAxisTitle="Percentage of build failures"
              isLoading={executionFailedModels.fetching}
              items={rawData}
              error={executionFailedModels.error?.message}
              getTooltip={(entry: PercentageFailureEntry) => {
                const nodeIsDeleted = entry.isDeleted;
                const totalExecutions = entry.totalExecutions || 0;
                const failedExecutions = entry.totalFailedExecutions || 0;
                return (
                  <div>
                    <div>
                      Failed to build {failedExecutions} out of {totalExecutions} times
                      ({entry.failurePercentage || 0})
                    </div>
                    {nodeIsDeleted && (
                      <div>
                        <i>This is a deleted model.</i>
                      </div>
                    )}
                  </div>
                );
              }}
              testId="most-failed-models-by-build-chart"
            />
          )}
          {activeTab === 'test' && (
            <PercentageFailureChart
              missingDataMessage="None of your models have failed tests in this time period."
              xAxisTitle="Percentage of test failures"
              isLoading={testFailedModels.fetching}
              items={rawData}
              error={testFailedModels.error?.message}
              getTooltip={(entry: PercentageFailureEntry) => {
                const nodeIsDeleted = entry.isDeleted;
                const totalExecutions = entry.totalExecutions || 0;
                const failedExecutions = entry.totalFailedExecutions || 0;
                return (
                  <div>
                    <div>
                      Failed tests in {failedExecutions} out of {totalExecutions}{' '}
                      executions ({entry.failurePercentage || 0}%)
                    </div>
                    {nodeIsDeleted && (
                      <div>
                        <i>This is a deleted model.</i>
                      </div>
                    )}
                  </div>
                );
              }}
              testId="most-failed-models-by-test-chart"
            />
          )}
        </div>
      </div>
    </Card>
  );
};
